import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, LogOut, ExternalLink, ImageOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api, { resolveMedia } from '../lib/api';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger
} from '../components/ui/alert-dialog';

const statusStyles = {
  Available: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  Beta: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  'Coming soon': 'bg-white/5 text-slate-300 border-white/15'
};

const AdminDashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/products');
      setProducts(data);
    } catch (e) {
      toast({ title: 'Failed to load products' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast({ title: 'Product deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail });
    }
  };

  return (
    <div className="min-h-screen bg-[#05080f]">
      <header className="border-b border-white/5 bg-[#05080f]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-slate-400 hover:text-white">← Site</Link>
            <div className="h-4 w-px bg-white/10"></div>
            <h1 className="text-white font-display text-lg">Admin Console</h1>
            <div className="h-4 w-px bg-white/10 hidden md:block"></div>
            <Link to="/admin/consultations" className="hidden md:inline text-sm text-slate-300 hover:text-amber-300">
              Consultations
            </Link>
            <Link to="/admin/demo-requests" className="hidden md:inline text-sm text-slate-300 hover:text-amber-300">
              Demo Requests
            </Link>
            <Link to="/admin/content-studio" className="hidden md:inline text-sm text-slate-300 hover:text-amber-300">
              Content Studio
            </Link>
            <Link to="/admin/case-studies" className="hidden md:inline text-sm text-slate-300 hover:text-amber-300">
              Case Studies
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-white text-sm">{user?.username}</div>
              <div className="text-slate-500 text-xs">{user?.email}</div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10">
              <LogOut size={14} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">AI Products</h2>
            <p className="text-slate-400 mt-1 text-sm">Manage all AI products shown on the public site.</p>
          </div>
          <Link to="/admin/products/new">
            <Button className="btn-gradient rounded-full h-10 px-5 text-white border-0">
              <Plus size={16} className="mr-2" /> New product
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-slate-400 py-16 text-center">Loading products...</div>
        ) : products.length === 0 ? (
          <div className="card-dark rounded-3xl p-12 text-center">
            <p className="text-slate-300">No products yet. Add your first one to get started.</p>
            <Link to="/admin/products/new">
              <Button className="btn-gradient rounded-full h-10 px-5 text-white border-0 mt-5">
                <Plus size={16} className="mr-2" /> Create product
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {products.map((p) => (
              <div key={p.id} className="card-dark rounded-2xl p-5 flex flex-col md:flex-row gap-5 items-start">
                <div className="h-20 w-32 shrink-0 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
                  {p.thumbnail ? (
                    <img src={resolveMedia(p.thumbnail)} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <ImageOff size={18} className="text-slate-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-white font-semibold">{p.name}</h3>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusStyles[p.status] || statusStyles['Coming soon']}`}>{p.status}</span>
                  </div>
                  <div className="text-amber-400 text-sm mt-0.5">{p.tagline}</div>
                  <p className="text-slate-400 text-sm mt-2 line-clamp-2">{p.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {p.tags?.map((t) => (
                      <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-300 border border-white/10">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 md:self-center">
                  <Link to="/ai-products" target="_blank">
                    <Button size="sm" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10">
                      <ExternalLink size={14} />
                    </Button>
                  </Link>
                  <Link to={`/admin/products/${p.id}/edit`}>
                    <Button size="sm" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10">
                      <Pencil size={14} />
                    </Button>
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20">
                        <Trash2 size={14} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#0b1220] border-white/10 text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete product?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          This will permanently remove “{p.name}” from your site.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(p.id)} className="bg-red-500/80 hover:bg-red-500 text-white">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboardPage;
